import {
  type CreateKlarnaOrderPayload,
  type KlarnaOrder,
  type Option,
  type OrderDTO,
  type PaymentProviderConfig,
  type ShippingProviderConfig
} from 'ecosystem'
import { klarnaApiCall } from '../../../utils/klarnaApiCall'
import { getStorefrontOrderByOrderNumber } from '../getStorefrontOrderByOrderNumber'
import { alertService } from '../../../utils/alert-service'
import { completeStorefrontOrder } from './completeStorfrontOrder'
import {
  generateIngridShippingProviderResult,
  generateNshiftShippingProviderResult,
  generatePostnordShippingProviderResult,
  isOrderAlreadyProvisioned,
  isOrderFresh
} from './utils'
import { type OrderProvisionResult, type ProvisionOrderOptions } from './types'

export interface ProvisionOrderKlarnaProviderResult {
  klarnaOrder: KlarnaOrder
  storefrontOrder: Option<OrderDTO>
  shippingProviderResult?: OrderProvisionResult['shippingProviderResult']
}

export const provisionOrderKlarnaProvider = async (
  orderId: string,
  paymentProviderConfig?: PaymentProviderConfig,
  shippingProviderConfig?: ShippingProviderConfig,
  options?: ProvisionOrderOptions
): Promise<Option<ProvisionOrderKlarnaProviderResult>> => {
  // 1. Confirmation in Klarna
  const { error: confirmationError, data: klarnaOrderData } = await klarnaApiCall<
    KlarnaOrder,
    CreateKlarnaOrderPayload
  >('get', {
    orderId
  })

  if (confirmationError || !klarnaOrderData) {
    void alertService.send('Provision order | Get klarna order', {
      blocks: [
        {
          title: 'Payload',
          content: {
            orderId
          }
        },
        {
          title: 'Error',
          content: confirmationError
        }
      ]
    })

    throw Error(confirmationError || 'Klarna GET endpoint error')
  }

  // 2. Checks if the order exists
  const { error: getOrderError, data: orderData } = await getStorefrontOrderByOrderNumber({
    orderNumber: klarnaOrderData.merchant_reference1
  })

  // 3. If the order doesn't exist, returns an error
  if (getOrderError || !orderData) {
    void alertService.send('Provision order | Get storefront order', {
      blocks: [
        {
          title: 'Payload',
          content: {
            orderNumber: klarnaOrderData.merchant_reference1
          }
        },
        {
          title: 'Klarna Order Data',
          content: klarnaOrderData
        },
        {
          title: 'Error',
          content: getOrderError
        }
      ]
    })
    throw Error(confirmationError || 'BE GET endpoint error')
  }

  // Idempotency guard: the order is provisioned exactly once. If it is no longer CREATED, another
  // path (confirmation page, or an earlier push retry) already provisioned it — return the existing
  // order without re-booking shipment or re-writing it, to avoid duplicates.
  if (isOrderAlreadyProvisioned(orderData)) {
    return { storefrontOrder: orderData, klarnaOrder: klarnaOrderData }
  }

  // Fallback gate (payment push only): a still-fresh order belongs to the confirmation page; throw
  // so the push endpoint responds non-2xx and the provider retries later. This keeps the page the
  // single provisioner during the post-checkout window, eliminating the page-vs-push race.
  if (options?.asFallback && isOrderFresh(orderData)) {
    throw Error('Payment push received for a still-fresh order; deferring to the confirmation page')
  }

  const getShippingProviderResult = async (): Promise<
    OrderProvisionResult['shippingProviderResult'] | void
  > => {
    if (!orderData?.shippingProvider) {
      return
    }

    switch (orderData?.shippingProvider) {
      case 'INGRID':
        return generateIngridShippingProviderResult(orderData)
      case 'POSTNORD': {
        return generatePostnordShippingProviderResult(
          orderData,
          {
            phoneNo: klarnaOrderData.billing_address.phone,
            smsNo: klarnaOrderData.billing_address.phone,
            emailAddress: klarnaOrderData.billing_address.email,
            contactName: `${klarnaOrderData.billing_address.given_name} ${klarnaOrderData.billing_address.family_name}`
          },
          shippingProviderConfig
        )
      }
      case 'NSHIFT': {
        return generateNshiftShippingProviderResult(orderData, {
          phone: klarnaOrderData.billing_address.phone,
          email: klarnaOrderData.billing_address.email,
          name: `${klarnaOrderData.billing_address.given_name} ${klarnaOrderData.billing_address.family_name}`
        })
      }
      case 'DEFAULT':
    }
  }

  // 4. Get shipping provider information
  const shippingProviderResult = await getShippingProviderResult()

  // 5. Complete storefront order, change status to PAID and update all info
  const storefrontOrder = await completeStorefrontOrder({
    order: orderData,
    paymentProviderInfo: {
      ...paymentProviderConfig,
      providerName: 'KLARNA',
      providerResponse: {
        klarnaResponse: klarnaOrderData
      }
    },
    ...(shippingProviderResult && {
      shippingProviderInfo: shippingProviderResult
    })
  })

  // 6. Acknowledge Order to Klarna
  const { error: acknowledgementError } = await klarnaApiCall<null, undefined>('acknowledge', {
    orderId
  })

  if (acknowledgementError) {
    void alertService.send('Provision order | Klarna acknowledge order', {
      blocks: [
        {
          title: 'Payload',
          content: {
            orderId
          }
        },
        {
          title: 'Klarna Order Data',
          content: klarnaOrderData
        },
        {
          title: 'Completed Storefront Order',
          content: storefrontOrder
        },
        {
          title: 'Error',
          content: acknowledgementError
        }
      ]
    })
    throw Error(acknowledgementError)
  }

  return {
    storefrontOrder,
    klarnaOrder: klarnaOrderData,
    ...(shippingProviderResult && {
      shippingProviderResult
    })
  }
}
